import _ from 'lodash'

export type Paginated<T> = {
  items: T[]
  currentPage: number
  numberOfPages: number
  totalItems: number
}

export type PaginationSearchParams = {
  page?: string | string[]
}

/**
 * Slices a list down to a single page on the server.
 *
 * Paginating in a client component means every item has to be serialised into the RSC payload so
 * the browser can slice it — the blog overview shipped all 517 articles to render 10. Doing it
 * here keeps the payload proportional to what is actually rendered.
 *
 * An out-of-range, missing or malformed `page` is clamped rather than 404'd, so a stale link to
 * page 40 of a list that shrank still renders the last page.
 */
export const paginate = <T>(
  items: T[],
  perPage: number,
  page?: PaginationSearchParams['page'],
): Paginated<T> => {
  const numberOfPages = Math.max(1, Math.ceil(items.length / perPage))
  const requested = parseInt(Array.isArray(page) ? page[0] : page ?? '', 10)
  const currentPage = isNaN(requested) ? 1 : _.clamp(requested, 1, numberOfPages)
  const firstItem = (currentPage - 1) * perPage

  return {
    items: items.slice(firstItem, firstItem + perPage),
    currentPage,
    numberOfPages,
    totalItems: items.length,
  }
}

/**
 * Builds the URL for a given page, keeping the rest of the current query string intact.
 *
 * `omitPageOneParam` drops the key entirely for page 1 rather than writing `?page=1`, so the
 * first page keeps a clean URL. Callers that always want the param present (e.g. to match an
 * existing bookmarked link) can leave it off.
 */
export const buildPaginationUrl = (
  path: string,
  currentParams: URLSearchParams,
  page: number,
  paginationKey: string,
  omitPageOneParam = false,
) => {
  const params = new URLSearchParams(currentParams)
  if (omitPageOneParam && page <= 1) {
    params.delete(paginationKey)
  } else {
    params.set(paginationKey, String(page))
  }
  const query = params.toString()
  return query ? `${path}?${query}` : path
}
